import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { HiMenuAlt3 } from "react-icons/hi";
import { MdLogout, MdOutlineDashboard, MdAssessment, MdKeyboardArrowDown, MdKeyboardArrowRight, MdSwapHoriz } from "react-icons/md";
import { AiOutlineBars, AiOutlineUser } from "react-icons/ai";
import { TbLocation } from "react-icons/tb";
import Logo from "../../components/logo/logo";
import { useDispatch, useSelector } from "react-redux";
import { removeAuthInfo } from "../../store/slices/authSlice";
import { RiAdminLine, RiShieldUserLine, RiShieldLine } from "react-icons/ri";
import { FaFileInvoice, FaCog, FaClipboardList, FaUserMd, FaPills } from "react-icons/fa";
import SidebarPermissionWrapper from "./SidebarPermissionWrapper";

const SideBar = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const permissions = useSelector((state) => state.auth.permissions || []);
    const [showPopup, setShowPopup] = useState(false);
    const [open, setOpen] = useState(false);
    const [subMenuOpen, setSubMenuOpen] = useState({});
    const location = useLocation();

    // Helper function to check if user has permission
    const hasPermission = (permissionSlug) => {
        if (!permissionSlug) return true; // No permission required
        return permissions.some(perm => perm.slug === permissionSlug);
    };

    // Helper function to check if at least one child has permission (for parent menus)
    const hasAnyChildPermission = (subMenus) => {
        if (!subMenus || subMenus.length === 0) return true;
        return subMenus.some(subMenu => hasPermission(subMenu.permission));
    };

    const handleLogout = () => {
        setShowPopup(true);
    };

    const confirmLogout = () => {
        dispatch(removeAuthInfo());
        localStorage.clear();
        navigate("/login");
    };

    const cancelLogout = () => {
        setShowPopup(false);
    };

    const toggleSubMenu = (index) => {
        setSubMenuOpen((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
        if (!open) {
            setOpen(true);
        }
    };

    const menus = [
        { name: "Dashboard", link: "/dashboard", icon: MdOutlineDashboard, permission: "dashboard" },
        { name: "Organization List", link: "/organization-list", icon: AiOutlineBars, permission: "organization-list" },
        { name: "Doctor List", link: "/doctor-list", icon: AiOutlineUser, permission: "doctor-list" },
        { name: "Order List", link: "/order-list", icon: FaFileInvoice, permission: "order-list" },
        { name: "Follow Up Sheet Data", link: "/followup-test-list", icon: FaClipboardList, permission: "follow-up-sheet-data" },
        { name: "Doctor Data Transfer", link: "/doctor-data-transfer", icon: MdSwapHoriz, permission: "doctor-data-transfer" },
        { name: "Doctor Speciality", link: "/speciality-list", icon: FaUserMd, permission: "doctor-speciality" },
        { name: "Medicine List", link: "/medicine-list", icon: FaPills, permission: "medicine-list" },
        {
            name: "Report",
            icon: MdAssessment,
            permission: null, // Report menu doesn't need permission check, but submenus might
            subMenus: [
                { name: "Medicine Most use", link: "/report/most-prescribed-medicines", icon: MdAssessment, permission: "medicine-most-use" },
                { name: "Appointment stats", link: "/report/doctor-wise-appointment-stats", icon: MdAssessment, permission: "appointment-stats" },
            ],
        },
        {
            name: "Administration",
            icon: FaCog,
            permission: null, // Administration menu doesn't need permission check, but submenus do
            subMenus: [
                { name: "Admin List", link: "/admin-list", icon: RiShieldUserLine, permission: "admin-list" },
                { name: "Roles", link: "/roles", icon: RiShieldUserLine, permission: "roles" },
                { name: "Permissions", link: "/permissions", icon: RiShieldLine, permission: "permissions" },
            ],
        },
    ];

    return (
        <div className=" flex h-screen z-[999]">
            <div
                className={`bg-primary flex flex-col justify-between ${open ? "w-60" : "w-16"
                    } duration-500 text-gray-100 px-4`}
            >
                {/* Logo and Menu Toggle */}
                <div>
                    {/*Menu Logo*/}
                    <div className="py-3 flex items-center justify-between">
                        <div className={`overflow-hidden transition-all duration-700 ${open ? "w-28" : "w-0"}`}>
                            <Logo />
                        </div>
                        <HiMenuAlt3
                            size={26}
                            className="cursor-pointer"
                            onClick={() => setOpen(!open)}
                        />
                    </div>
                    {/* Menu Items */}
                    <div className="mt-4 flex flex-col gap-4">
                        {menus.map((menu, i) => {
                            const isActive = location.pathname === menu.link;
                            const hasSubMenu = menu.subMenus && menu.subMenus.length > 0;
                            const isSubMenuOpen = subMenuOpen[i];

                            // Check if parent menu should be shown
                            // If menu has permission, check it; if it has submenus, check if at least one child has permission
                            const shouldShowParent = hasSubMenu 
                                ? hasAnyChildPermission(menu.subMenus)
                                : hasPermission(menu.permission);

                            // Don't render parent if it shouldn't be shown
                            if (!shouldShowParent) {
                                return null;
                            }

                            // Filter submenus based on permissions (only show ones with permission)
                            const filteredSubMenus = hasSubMenu 
                                ? menu.subMenus.filter(subMenu => hasPermission(subMenu.permission))
                                : [];

                            return (
                                <SidebarPermissionWrapper key={i} permission={menu.permission}>
                                    <div>
                                        <div
                                            className={`group flex items-center text-sm gap-3.5 font-medium p-2 rounded-md transition-all duration-500 cursor-pointer ${isActive && !hasSubMenu ? "bg-gray-800 text-white" : "hover:bg-gray-700"
                                                }`}
                                            onClick={() => {
                                                if (hasSubMenu) {
                                                    toggleSubMenu(i);
                                                } else {
                                                    navigate(menu.link);
                                                }
                                            }}
                                        >
                                            <div>{React.createElement(menu.icon, { size: "20" })}</div>
                                            <h2
                                                style={{
                                                    transitionDelay: `${i + 3}00ms`,
                                                }}
                                                className={`whitespace-pre duration-300 ${!open && "opacity-0 translate-x-28 overflow-hidden"
                                                    }`}
                                            >
                                                {menu.name}
                                            </h2>

                                            {hasSubMenu && filteredSubMenus.length > 0 && open && (
                                                <div className="ml-auto">
                                                    {isSubMenuOpen ? <MdKeyboardArrowDown /> : <MdKeyboardArrowRight />}
                                                </div>
                                            )}

                                            <h2
                                                className={`${open && "hidden"
                                                    } absolute left-48 bg-white font-semi-bold whitespace-pre text-gray-900 rounded-md drop-shadow-lg px-0 py-0 w-0 overflow-hidden group-hover:px-2 group-hover:py-1 group-hover:left-14 group-hover:duration-300 group-hover:w-fit`}
                                            >
                                                {menu.name}
                                            </h2>
                                        </div>

                                        {/* Submenu Items */}
                                        {hasSubMenu && isSubMenuOpen && open && filteredSubMenus.length > 0 && (
                                            <div className="ml-8 mt-2 flex flex-col gap-2">
                                                {filteredSubMenus.map((subMenu, j) => {
                                                    const isSubActive = location.pathname === subMenu.link;
                                                    return (
                                                        <Link
                                                            key={j}
                                                            to={subMenu.link}
                                                            className={`flex items-center text-sm gap-3.5 font-medium p-2 rounded-md transition-all duration-500 ${isSubActive ? "bg-gray-800 text-white" : "hover:bg-gray-700"
                                                                }`}
                                                        >
                                                            <span className="text-lg">•</span>
                                                            <h2 className="whitespace-pre duration-300">
                                                                {subMenu.name}
                                                            </h2>
                                                        </Link>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </SidebarPermissionWrapper>
                            );
                        })}
                    </div>
                </div>

                {/* Logout Button */}
                <div className="mb-4">
                    <div
                        className={`group flex bg-secondary cursor-pointer items-center text-sm gap-3.5 font-medium p-2 rounded-md transition-all duration-500 hover:bg-gray-700`}
                        onClick={handleLogout}
                    >
                        <div>{React.createElement(MdLogout, { size: "20" })}</div>
                        <h2
                            style={{
                                transitionDelay: `${menus.length + 3}00ms`,
                            }}
                            className={`whitespace-pre duration-300 ${!open && "opacity-0 translate-x-10 overflow-hidden"
                                }`}
                        >
                            Logout
                        </h2>
                        <h2
                            className={`${open && "hidden"
                                } absolute left-48 bg-white font-semi-bold whitespace-pre text-gray-900 rounded-md drop-shadow-lg px-0 py-0 w-0 overflow-hidden group-hover:px-2 group-hover:py-1 group-hover:left-14 group-hover:duration-300 group-hover:w-fit`}
                        >
                            Logout
                        </h2>
                    </div>
                </div>

                {showPopup && (
                    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
                        <div className="flex flex-col items-center bg-primary p-5 rounded-lg shadow-lg">
                            <p>Are you sure you want to log out?</p>
                            <div className="mt-4 flex justify-end space-x-2">
                                <button onClick={confirmLogout} className="bg-red-500 text-white px-4 py-2 rounded">
                                    Yes, Logout
                                </button>
                                <button onClick={cancelLogout} className="bg-white text-black px-4 py-2 rounded">
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default SideBar;
